<template>
    <div class="conclusion p-3 mt-3">
        <div class="attachments d-flex justify-content-start flex-wrap">
            <div class="position-relative me-3 mb-3" v-for="(image, index) in model.images">
                <div
                    class="d-flex position-absolute w-100 h-100 top-0 left-0 justify-content-center align-items-center">
                    <button type="button" class="btn btn-sm btn-delete btn-secondary  btn-icon" v-on:click="model.images.splice(index, 1)">
                        <i class="fs-16 fi fi-rr-trash"></i>
                    </button>
                </div>
                <img class="border-radius-10" :src="toUrl(image)" height="70" width="70"
                     alt="Img" style="object-fit: cover;">
            </div>
        </div>
        <textarea @input="handleAdjustHeight($refs.description)" v-model="model.description" @focus="focusOnAIInput" :placeholder="trans('general.module.report.update_popup.step.details.fields.description')"
                  type="text" ref="description" class="ai-input form-control" id="description" name="description"></textarea>

        <div class="row">
        <span>
            <input class="d-none" id="chooseImages" type="file" accept="image/*"
                   v-on:change="selectedImages($event)" multiple/>
            <input class="d-none" id="captureImage" type="file" accept="image/*"
                   v-on:change="selectedImages($event)" capture="camera"/>
        </span>

        <div class="col-12 icons d-flex align-items-center">
            <div class="icon" v-if="allowImages" onclick="document.getElementById('chooseImages').click()" type="button">
                <i class="icon fi fi-rr-picture"></i>
            </div>
            <div class="icon" v-if="allowImages" onclick="document.getElementById('captureImage').click()" type="button">
                <i class="icon fi fi-rr-camera"></i>
            </div>

            <a-i-button
                v-if="allowCorrection"
                icon="fi-rr-text-check"
                :disabled="model.description?.length === 0 || generatingAI"
                :generating="generatingAICorrection"
                :text="trans('general.ai.correct_text')"
                v-on:click="model.description?.length > 0 ? correctText() : null"/>

            <a-i-button
                v-if="allowConclusion"
                icon="fi-rr-artificial-intelligence"
                :disabled="generatingAI"
                :generating="generatingAIConclusion"
                :text="trans('general.ai.help_me_write')"
                v-on:click="suggestText"/>
        </div>
    </div>
    </div>
</template>

<script>

import AIButton from "../../../theme/atoms/AIButton.vue";
import {getLanguageFromKey, REPORT_TYPE} from "../../../services/Defaults";
import {adjustHeight, focusOnAIInput, startTyping} from "../../../services/AI";
import {getSessionSpace} from "../../../services/SessionService";

export default {
    name: "AdditionalInfo",
    components: {AIButton},
    props: {
        value: {
            type: Object,
            required: true
        },
        allowImages: {
            type: Boolean,
            default: true,
            required: false,
        },
        allowCorrection: {
            type: Boolean,
            default: true,
            required: false,
        },
        allowConclusion: {
            type: Boolean,
            default: true,
            required: false,
        },
    },
    data() {
        return {
            model: {
                ...this.value,
                description: '',
                images: [],
            },

            generatingAI: false,
            generatingAICorrection: false,
            generatingAIConclusion: false,
            internalUpdate: false,
        };
    },
    watch: {
        value: {
            handler(newValue) {
                if (!this.internalUpdate) {
                    this.model = {...this.model, ...newValue};
                }
                this.internalUpdate = false;
            },
            deep: true,
            immediate: true,
        },
        model: {
            handler: function (value) {
                this.internalUpdate = true;
                this.$emit('input', value);
            },
            deep: true
        },
        additionalInfo() {
            this.$nextTick(() => {
                adjustHeight(this.$refs.description);
            });
        },
    },
    methods: {
        getLanguageFromKey, focusOnAIInput,

        generatedReport(type, childPronounce = 'child') {
            if(type === REPORT_TYPE.NAP || type === REPORT_TYPE.OUTSIDE) {
                let duration = ` from ${this.hoursFromSelectedOption.value}:${this.minutesFromSelectedOption.value} to ${this.hoursToSelectedOption.value}:${this.minutesToSelectedOption.value}`;

                if(type === REPORT_TYPE.OUTSIDE) {
                    return `Outside${duration}`;
                }

                return `${childPronounce} ${this.hasSlept ? 'has slept' + (this.addSleepDuration ? duration : '' ) : 'didn\'t sleep'}`;

            } else if(type === REPORT_TYPE.BATHROOM) {
                if(this.selectedBathroomTypeOption.value === 'other') return '';
                return `${childPronounce} had ${this.selectedBathroomTypeOption.value}`;

            } else if(type === REPORT_TYPE.MOOD) {
                if(this.mood && this.mood.value !== 'other') {
                    return `${childPronounce}'s mood is ${this.mood.value}`;
                }
                return '';

            } else if(type === 'activity') {
                if(this.activity) {
                    return `${childPronounce} did "${this.activity.label}" activity`;
                }
                return `${childPronounce} did an activity`;

            } else if(type === REPORT_TYPE.MEAL) {
                if (this.classMenu) {
                    let quantity = '';
                    if (this.mealQtyId) {
                        let qty = this.mealQuantities.find(item => item.id === this.mealQtyId);
                        quantity = `quantity eaten: ${qty.label}`;
                    }

                    let menu = this.classMenu.data;
                    let meal = menu.options[0].label;
                    return `${childPronounce} has eaten meal: ${meal}. ${quantity}`;

                }
            } else if(type === REPORT_TYPE.HEALTH) {
                if (this.health_condition.OPTIONS != null) {
                    let selected = [];
                    this.selectedHealthOptions.forEach(item => {
                        selected.push(item.value.replace(/_/g, ' '));
                    });
                    return `${childPronounce} had symptoms: ${selected.join(', ')}`;
                } else {
                    return `${childPronounce} got a pediatric control`;
                }
            } else if(type === REPORT_TYPE.HYDRATION) {
                let hydration = `${childPronounce} drank water`;
                if(this.hydration.value) {
                    return `${hydration} in the ${this.hydration.value === 'am' ? 'morning' : 'afternoon'}`;
                }
            }

            return '';
        },

        correctText() {
            if(! this.generatingAI) {
                this.generatingAI = true;
                this.generatingAICorrection = true;

                const prompt = `Please apply correction to the text in the written language and return just the result. Here is the text: ${this.model.description}.`;

                axios.post('/general-ai/manipulate-text', {prompt, type: 'correction'})
                    .then(response => {
                        this.generatingAICorrection = false;
                        this.generatingAI = false;
                        this.setAdditionalText(response.data.data);
                    })
                    .catch(error => {
                        this.$notify({type: 'error', text: trans('general.ai.error_correcting_text')});
                        this.generatingAICorrection = false;
                        this.generatingAI = false;
                    })
            }
        },

        async suggestText() {
            if(! this.generatingAI) {
                this.generatingAI = true;
                this.generatingAISuggestion = true;

                let childPronounce;
                const space = await getSessionSpace();
                let lang = this.getLanguageFromKey(space.lang);
                let teacherText = this.model.description !== '' ? `Include & rephrase: ${this.model.description}.` : '';
                let generatedReport = `"${this.generatedReport(this.type, childPronounce)}"`;
                let prompt = `You are reporting as teacher, in ${lang}. Please use only single attributes meaning "the child". ${generatedReport}. Make it sound human and personal! ${teacherText}`;

                axios.post('/general-ai/manipulate-text', {prompt: prompt, type: 'description'})
                    .then(response => {
                        this.generatingAISuggestion = false;
                        this.generatingAI = false;
                        this.setAdditionalText(response.data.data);
                    })
                    .catch(error => {
                        this.$notify({type: 'error', text: trans('general.ai.error_generating_conclusion')});
                        this.generatingAISuggestion = false;
                        this.generatingAI = false;
                    })
            }
        },

        startTypingAI(text, ref) {
            startTyping(text, (value) => {
                this.model.description = value;
            }, () => adjustHeight(ref));
        },

        handleAdjustHeight(ref) {adjustHeight(ref)},

        setAdditionalText(text) {
            this.model.description = '';
            this.startTypingAI(text, this.$refs.description);
        },

        toUrl(image) {
            return URL.createObjectURL(image)
        },

        selectedImages(event) {
            for (let file of event.target.files) {
                this.model.images.push(file)
            }
        },
    }
};

</script>
